import { Suspense } from "react";
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
} from "react-router-dom";
import { Loader2 } from "lucide-react";
import { lazyWithRetry as lazy } from "@/lib/lazyWithRetry";
import { RequireAuth } from "@/auth/RequireAuth";
import { AppShell } from "@/shell/AppShell";

// Eager — first paint paths
import { Login } from "@/pages/Login";
import { Dashboard } from "@/pages/mkt/Dashboard";

// Lazy — per-route chunks (marketplaces surfaces + kept infra pages)
const BrandDashboard = lazy(() =>
  import("@/pages/mkt/BrandDashboard").then((m) => ({ default: m.BrandDashboard })),
);
const Library = lazy(() => import("@/pages/mkt/Library").then((m) => ({ default: m.Library })));
const Optimize = lazy(() => import("@/pages/mkt/Optimize").then((m) => ({ default: m.Optimize })));
const Trends = lazy(() => import("@/pages/mkt/Trends").then((m) => ({ default: m.Trends })));
const Reports = lazy(() => import("@/pages/mkt/Reports").then((m) => ({ default: m.Reports })));
const ReportsHub = lazy(() =>
  import("@/pages/mkt/ReportsHub").then((m) => ({ default: m.ReportsHub })),
);
const WeeklyReport = lazy(() =>
  import("@/pages/mkt/WeeklyReport").then((m) => ({ default: m.WeeklyReport })),
);
const Amc = lazy(() => import("@/pages/mkt/Amc").then((m) => ({ default: m.Amc })));
const AnalyticsDashboard = lazy(() =>
  import("@/pages/analytics/AnalyticsDashboard").then((m) => ({ default: m.AnalyticsDashboard })),
);
const AuditLog = lazy(() => import("@/pages/mkt/AuditLog").then((m) => ({ default: m.AuditLog })));
const Goals = lazy(() => import("@/pages/mkt/Goals").then((m) => ({ default: m.Goals })));
const Chat = lazy(() => import("@/pages/Chat").then((m) => ({ default: m.Chat })));
const Portfolio = lazy(() => import("@/pages/Portfolio").then((m) => ({ default: m.Portfolio })));
const Onboard = lazy(() => import("@/pages/Onboard").then((m) => ({ default: m.Onboard })));
const OrgSettings = lazy(() => import("@/pages/OrgSettings").then((m) => ({ default: m.OrgSettings })));
const Admin = lazy(() => import("@/pages/Admin").then((m) => ({ default: m.Admin })));
const TikTokVideos = lazy(() =>
  import("@/pages/TikTokVideos").then((m) => ({ default: m.TikTokVideos })),
);
const TikTokProducts = lazy(() =>
  import("@/pages/TikTokProducts").then((m) => ({ default: m.TikTokProducts })),
);
const TikTokReturns = lazy(() =>
  import("@/pages/TikTokReturns").then((m) => ({ default: m.TikTokReturns })),
);
const TikTokLive = lazy(() => import("@/pages/TikTokLive").then((m) => ({ default: m.TikTokLive })));

function ChunkFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-bg">
      <Loader2 className="animate-spin text-text-muted" size={20} />
    </div>
  );
}

/** A brand opens on its Dashboard — ADR-0008. Every sibling IQ product lands
 *  an operator on performance; this one landed on a catalogue list of 11,483
 *  products with no metrics on it, which is why the product never felt
 *  performance-first regardless of the sidebar. */
function BrandIndexRedirect() {
  return <Navigate to="dashboard" replace />;
}

export function App() {
  return (
    <BrowserRouter>
      <Suspense fallback={<ChunkFallback />}>
        <Routes>
          <Route path="/login" element={<Login />} />

          <Route element={<RequireAuth><AppShell /></RequireAuth>}>
            {/* Post-login landing is the cross-brand Portfolio. /dashboard
                (the brand-cards page) stays mounted and reachable — the
                Portfolio adds an org-level view above it, it doesn't
                replace it. */}
            <Route index element={<Navigate to="/portfolio" replace />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/onboard" element={<Onboard />} />
            <Route path="/settings" element={<OrgSettings />} />
            <Route path="/admin" element={<Admin />} />

            <Route path="/brand/:slug">
              <Route index element={<BrandIndexRedirect />} />
              {/* ADR-0008 brand landing. AppShell's market picker was already
                  navigating here before the route existed (handleRegionChange)
                  — this completes that path rather than inventing one. */}
              <Route path="dashboard" element={<BrandDashboard />} />
              <Route path="library" element={<Library />} />
              <Route path="optimize" element={<Optimize />} />
              <Route path="trends" element={<Trends />} />
              {/* Reporting 3b. /reports was the BUILDER through 3a; it is now
                  the hub, and the builder lives one segment deeper. A report
                  that persists needs a home to be listed in, and the hub is
                  that home — anyone with the 3a page bookmarked lands on the
                  list rather than on an untitled builder.

                  `new` is declared before `:reportId` for legibility only —
                  React Router ranks a static segment above a dynamic
                  one regardless of source order (v7.18.1 installed;
                  `staticSegmentValue = 10` vs `dynamicSegmentValue = 3` in
                  `rankRouteBranches`, with source order used only to break a
                  tie between equal scores) — so `/reports/new` cannot be read
                  as a report id. */}
              <Route path="reports" element={<ReportsHub />} />
              {/* Code-defined standing report. Declared BEFORE reports/:reportId
                  so "weekly" is not swallowed by the id param. */}
              <Route path="reports/weekly" element={<WeeklyReport />} />
              <Route path="reports/new" element={<Reports />} />
              <Route path="reports/:reportId" element={<Reports />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              {/* AppShell's "All Markets" nav targets /overview; until a
                  dedicated brand-overview page exists, land on analytics
                  (was silently bouncing to /dashboard via the catch-all). */}
              <Route path="overview" element={<Navigate to="../analytics" replace />} />
              <Route path="amc" element={<Amc />} />
              <Route path="audit-log" element={<AuditLog />} />
              <Route path="goals" element={<Goals />} />
              {/* Full-page persistent advisor chat. Also serves the FloatingChat
                  widget body via ?embedded=1 (iframed by ChatPanel.tsx). */}
              <Route path="chat" element={<Chat />} />
              {/* TikTok Shop channel (ADR-0010). No :platform segment: no Amazon page shares these slugs. */}
              <Route path="tiktok/videos" element={<TikTokVideos />} />
              <Route path="tiktok/products" element={<TikTokProducts />} />
              <Route path="tiktok/returns" element={<TikTokReturns />} />
              <Route path="tiktok/live" element={<TikTokLive />} />
              {/* TikTok Shop advisor chat (Task 13). Same Chat component as the
                  Amazon /chat route; the page reads "tiktok_shop" from the path
                  and mounts the tiktok-shop-intent read-only tool surface. */}
              <Route path="tiktok/chat" element={<Chat />} />
            </Route>
          </Route>

          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </Suspense>
    </BrowserRouter>
  );
}
